import React from 'react';
import { useTranslation } from 'react-i18next';
import parashaList from '../../parasha_map.json';
import { Parasha } from '../utils/parashaNameMap';

const PARASHOT_BY_BOOK: Array<{
  bookEnglish: string;
  bookHebrew: string;
  parashot: Parasha[];
}> = [
  {
    bookEnglish: 'Bereshit (Genesis)',
    bookHebrew: 'בראשית',
    parashot: (parashaList as Parasha[]).slice(0, 12),
  },
  {
    bookEnglish: 'Shemot (Exodus)',
    bookHebrew: 'שמות',
    parashot: (parashaList as Parasha[]).slice(12, 23),
  },
  {
    bookEnglish: 'Vayikra (Leviticus)',
    bookHebrew: 'ויקרא',
    parashot: (parashaList as Parasha[]).slice(23, 33),
  },
  {
    bookEnglish: 'Bamidbar (Numbers)',
    bookHebrew: 'במדבר',
    parashot: (parashaList as Parasha[]).slice(33, 43),
  },
  {
    bookEnglish: 'Devarim (Deuteronomy)',
    bookHebrew: 'דברים',
    parashot: (parashaList as Parasha[]).slice(43),
  },
];

const TEXT_DARK = '#1A1A2E';
const BORDER_LIGHT = '#D1D5DB';
const BIRTHDATE_COLOR = '#0F264A';

export interface ParashaChooserProps {
  /** Current value: parasha english key or empty string */
  value: string;
  /** Called when user selects a parasha (or clears selection when variant allows) */
  onChange: (parasha: Parasha | null) => void;
  /** "full" = full width with optional label (e.g. BirthDatePage); "compact" = inline for header (e.g. PracticePage) */
  variant?: 'full' | 'compact';
  /** Show label above the select (used when variant="full") */
  showLabel?: boolean;
  /** Override aria-label */
  ariaLabel?: string;
  /** Optional extra style for the select container (variant=full) or the select (variant=compact) */
  style?: React.CSSProperties;
}

const ParashaChooser: React.FC<ParashaChooserProps> = ({
  value,
  onChange,
  variant = 'full',
  showLabel = true,
  ariaLabel,
  style,
}) => {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val) {
      onChange(null);
      return;
    }
    const parasha = (parashaList as Parasha[]).find((p) => p.english === val) ?? null;
    onChange(parasha);
  };

  const allowEmpty = variant === 'full';
  const isCompact = variant === 'compact';

  const baseSelectStyle: React.CSSProperties = isCompact
    ? {
        padding: '6px 12px',
        borderRadius: 12,
        border: `1px solid ${BORDER_LIGHT}`,
        fontSize: 16,
        fontWeight: 600,
        backgroundColor: 'white',
        color: TEXT_DARK,
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%231A1A2E' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: isHebrew ? 'left 10px center' : 'right 10px center',
        paddingRight: isHebrew ? '10px' : '32px',
        paddingLeft: isHebrew ? '32px' : '10px',
        direction: isHebrew ? 'rtl' : 'ltr',
      }
    : {
        width: '100%',
        padding: '12px 14px',
        borderRadius: 16,
        border: '1px solid #CED4DA',
        fontSize: 14,
        backgroundColor: 'white',
        color: BIRTHDATE_COLOR,
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%230F264A' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: isHebrew ? 'left 14px center' : 'right 14px center',
        paddingRight: isHebrew ? '14px' : '36px',
        paddingLeft: isHebrew ? '36px' : '14px',
        direction: isHebrew ? 'rtl' : 'ltr',
      };

  const selectEl = (
    <select
      value={value}
      onChange={handleChange}
      aria-label={ariaLabel ?? (t('choose_parasha_label') || 'Choose parasha')}
      style={{ ...baseSelectStyle, ...style }}
    >
      <option value="" disabled={!allowEmpty}>
        {t('search_parasha_placeholder') || 'Select a parasha...'}
      </option>
      {PARASHOT_BY_BOOK.map((book) => (
        <optgroup
          key={book.bookEnglish}
          label={`━━  📖 ${isHebrew ? book.bookHebrew : book.bookEnglish}  ━━`}
          style={{
            fontWeight: 900,
            fontSize: 15,
            color: isCompact ? TEXT_DARK : BIRTHDATE_COLOR,
            backgroundColor: '#F1E9D3',
            padding: '12px 8px',
            marginTop: '8px',
          }}
        >
          {book.parashot.map((p) => (
            <option
              key={p.english}
              value={p.english}
              style={{
                fontWeight: 400,
                color: isCompact ? TEXT_DARK : BIRTHDATE_COLOR,
                padding: '8px 0',
              }}
            >
              {isHebrew ? p.hebrew : p.english}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  if (isCompact) {
    return selectEl;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
      {showLabel && (
        <label style={{ fontSize: 12, fontWeight: 600, color: BIRTHDATE_COLOR }}>
          {t('choose_parasha_label')}
        </label>
      )}
      {selectEl}
    </div>
  );
};

export default ParashaChooser;
